import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ForkKnife, Sparkle, FilmStrip, BookOpen } from "@phosphor-icons/react";

export default function Landing() {
  const { user, loading } = useAuth();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Top nav */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="brand-logo">
          <ForkKnife size={26} weight="duotone" className="text-[hsl(var(--primary))]" />
          <span className="font-serif text-2xl tracking-tight">MIZETA</span>
        </div>
        <div className="flex items-center gap-3">
          {!loading && user ? (
            <Link to="/app" data-testid="go-to-app-link" className="px-5 py-2 rounded-full bg-[hsl(var(--foreground))] text-white text-sm font-medium btn-press hover-lift">
              Entrar a la cocina
            </Link>
          ) : (
            <button onClick={handleLogin} data-testid="header-login-btn" className="px-5 py-2 rounded-full bg-[hsl(var(--foreground))] text-white text-sm font-medium btn-press hover-lift">
              Iniciar sesión
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="eyebrow mb-6">de la pantalla · al plato</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Convierte un
            <br />
            <span className="italic text-[hsl(var(--primary))]">vídeo de receta</span>
            <br />
            en una ficha de cocina
          </h1>
          <p className="mt-7 max-w-xl text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
            Pega un enlace de Instagram, TikTok o YouTube. MIZETA encuentra la información útil de la receta y la convierte en ingredientes con cantidades, comensales y pasos, lista para cocinar y guardar.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={handleLogin}
              data-testid="hero-login-btn"
              className="px-7 py-4 rounded-full bg-[hsl(var(--primary))] text-white font-semibold btn-press hover-lift inline-flex items-center gap-2"
            >
              <Sparkle size={20} weight="fill" />
              Empezar gratis con Google
            </button>
            <a href="#how" className="px-7 py-4 rounded-full border border-[hsl(var(--border))] font-medium btn-press hover:bg-white/60">
              ¿Cómo funciona?
            </a>
          </div>

          <div className="mt-12 flex items-center gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <span className="inline-flex items-center gap-2"><FilmStrip size={18} /> Instagram · TikTok · YouTube</span>
            <span className="inline-flex items-center gap-2"><BookOpen size={18} /> Recetario propio</span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden grain shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1466637574441-749b8f19452f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGluZ3JlZGllbnRzJTIwY29va2luZyUyMG92ZXJoZWFkfGVufDB8fHx8MTc3ODg0MzEzMXww&ixlib=rb-4.1.0&q=85"
              alt="Mesa con ingredientes frescos"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="absolute -left-8 bottom-12 bg-white p-5 rounded-2xl shadow-xl max-w-[220px] hidden md:block">
            <p className="eyebrow text-[10px]">Receta extraída</p>
            <p className="font-serif text-xl mt-1 leading-tight">Pasta al pesto rústico</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">4 comensales · 25 min · 6 ingredientes</p>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 border-t border-[hsl(var(--border))]">
        <p className="eyebrow text-center mb-4">tres pasos</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-center tracking-tight max-w-3xl mx-auto">
          De un scroll en tu móvil <span className="italic">a una receta</span> en tu cocina.
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { n: "01", title: "Pega el enlace", desc: "Copia la URL de un Reel, TikTok o vídeo de YouTube con la receta." },
            { n: "02", title: "MIZETA lo destila", desc: "Analizamos la fuente disponible y descartamos bromas, saludos y comentarios irrelevantes." },
            { n: "03", title: "Cocina sin distracciones", desc: "Recibes ingredientes con cantidades, pasos numerados y trucos del chef." },
          ].map((s, i) => (
            <div key={i} className="fade-up" style={{ animationDelay: `${i * 100}ms` }}>
              <p className="font-serif italic text-5xl text-[hsl(var(--primary))]">{s.n}</p>
              <h3 className="font-serif text-2xl mt-3">{s.title}</h3>
              <p className="mt-3 text-[hsl(var(--muted-foreground))] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[hsl(var(--border))] py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
        <p>© {new Date().getFullYear()} MIZETA — Recetas en estado puro.</p>
        <Link to="/eliminar-cuenta" className="inline-block mt-3 hover:text-[hsl(var(--foreground))]" data-testid="public-delete-account-link">Eliminar cuenta y datos</Link>
      </footer>
    </div>
  );
}
